import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const NotFoundErrorPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(searchQuery?.trim())}`);
    }
  };

  const quickLinks = [
    { label: 'Return to Homepage', path: '/', icon: 'Home', primary: true },
    { label: 'Search Properties', path: '/search-results', icon: 'Search', primary: false },
    { label: 'Browse Professionals', path: '/professional-marketplace', icon: 'Users', primary: false },
    { label: 'Help Center', path: '/help-support', icon: 'HelpCircle', primary: false },
  ];

  const popularPages = [
    { label: 'Market Intelligence Reports', path: '/uae-market-intelligence-reports', icon: 'BarChart3', description: 'UAE property market insights' },
    { label: 'Professional Marketplace', path: '/professional-marketplace', icon: 'Briefcase', description: 'Find agents, lawyers & more' },
    { label: 'Property Analysis', path: '/dashboard', icon: 'TrendingUp', description: 'AI-powered price analysis' },
    { label: 'FAQ & Help', path: '/faq', icon: 'MessageCircle', description: 'Common questions answered' },
    { label: 'About ListingLens', path: '/about-us', icon: 'Info', description: 'Our mission and team' },
    { label: 'Contact Support', path: '/contact-us', icon: 'Mail', description: 'Get in touch with us' },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <nav className="border-b border-border px-4 md:px-8 h-16 flex items-center justify-between sticky top-0 bg-background/95 backdrop-blur-sm z-50">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/assets/images/logo_LL-1772022913346.jpeg"
            alt="ListingLens logo - AI-powered property intelligence platform"
            className="h-9 w-auto rounded-lg object-cover"
            style={{ boxShadow: '0 0 12px rgba(0,212,255,0.4)', border: '1px solid rgba(0,212,255,0.3)', maxWidth: '130px' }}
          />
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors hidden sm:block"
          >
            Home
          </Link>
          <Link
            to="/contact-us"
            className="text-sm px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Contact Support
          </Link>
        </div>
      </nav>
      <div className="flex-1 flex flex-col">
        {/* Hero Error Section */}
        <div className="flex flex-col items-center justify-center px-4 py-16 md:py-24 text-center">
          {/* 404 Visual */}
          <div className="relative mb-8">
            <h1 className="text-[120px] md:text-[160px] font-black text-primary/8 select-none leading-none">
              404
            </h1>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-24 h-24 bg-primary/10 rounded-3xl flex items-center justify-center shadow-lg">
                <Icon name="MapPinOff" size={48} color="var(--color-primary)" />
              </div>
            </div>
          </div>

          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
            Oops! This Page Has Gone Off-Market
          </h2>
          <p className="text-muted-foreground max-w-lg mb-3 leading-relaxed text-base md:text-lg">
            We couldn't find the page you were looking for. It may have been moved, renamed, or no longer exists.
          </p>
          <p className="text-muted-foreground max-w-md mb-10 text-sm">
            Don't worry — let's get you back to finding your perfect property.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="w-full max-w-lg mb-10">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Icon
                  name="Search"
                  size={18}
                  color="var(--color-muted-foreground)"
                  className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search properties, suburbs, postcodes..."
                  className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
              </div>
              <button
                type="submit"
                className="px-5 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
              >
                Search
              </button>
            </div>
          </form>

          {/* Primary Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mb-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center gap-2 px-6 py-3 border border-border text-foreground rounded-xl font-medium hover:bg-card transition-colors"
            >
              <Icon name="ArrowLeft" size={18} />
              Go Back
            </button>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="Home" size={18} />
              Return to Homepage
            </Link>
            <Link
              to="/search-results"
              className="flex items-center justify-center gap-2 px-6 py-3 border border-primary/40 text-primary rounded-xl font-medium hover:bg-primary/5 transition-colors"
            >
              <Icon name="Search" size={18} />
              Search Properties
            </Link>
          </div>
        </div>

        {/* Popular Pages Section */}
        <div className="bg-card border-t border-border px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-lg md:text-xl font-semibold text-foreground text-center mb-2">
              Popular Pages
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-8">
              Here are some commonly visited sections that might help you
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {popularPages?.map((page) => (
                <Link
                  key={page?.path}
                  to={page?.path}
                  className="flex items-start gap-4 p-4 bg-background border border-border rounded-xl hover:border-primary/40 hover:shadow-md transition-all group"
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Icon name={page?.icon} size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      {page?.label}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {page?.description}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="px-4 md:px-8 py-10 md:py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
              <Icon name="Headphones" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-primary">Need Help?</span>
            </div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3">
              Still can't find what you're looking for?
            </h3>
            <p className="text-muted-foreground text-sm mb-6">
              Our support team is here to help. Reach out and we'll get you sorted quickly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact-us"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors"
              >
                <Icon name="Mail" size={18} />
                Contact Support
              </Link>
              <Link
                to="/faq"
                className="flex items-center justify-center gap-2 px-6 py-3 border border-border text-foreground rounded-xl font-medium hover:bg-card transition-colors"
              >
                <Icon name="HelpCircle" size={18} />
                Browse FAQ
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="border-t border-border px-4 md:px-8 py-6">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground text-center sm:text-left">
            &copy; {new Date()?.getFullYear()} ListingLens. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="text-xs text-muted-foreground hover:text-primary transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="text-xs text-muted-foreground hover:text-primary transition-colors">Terms</Link>
            <Link to="/contact-us" className="text-xs text-muted-foreground hover:text-primary transition-colors">Contact</Link>
            <Link to="/help-support" className="text-xs text-muted-foreground hover:text-primary transition-colors">Help</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NotFoundErrorPage;
